import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { searchNotificationApi } from '../../../constant/Constant';
import { Bell, Send, Users, User, ChevronLeft, ChevronRight, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const NotificationMaster = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, [page, filterType]);

  const fetchNotifications = () => {
    setLoading(true);
    const search = {};
    if (filterType) search.type = filterType;
    if (searchText) search.title = { $regex: searchText, $options: 'i' };

    HitApi({ page, limit: 20, search, sort: { createdAt: -1 } }, searchNotificationApi)
      .then((res) => {
        if (res?.data?.docs) {
          setNotifications(res.data.docs);
          setTotalPages(res.data.totalPages || 1);
          setTotalDocs(res.data.totalDocs || 0);
        }
      })
      .catch(() => toast.error('Failed to fetch notifications'))
      .finally(() => setLoading(false));
  };

  const handleSearch = () => {
    setPage(1);
    fetchNotifications();
  };

  const getTypeColor = (type) => {
    const colors = {
      booking: 'bg-blue-100 text-blue-700',
      payment: 'bg-green-100 text-green-700',
      promotion: 'bg-purple-100 text-purple-700',
      reminder: 'bg-yellow-100 text-yellow-700',
      general: 'bg-gray-100 text-gray-700',
    };
    return colors[type] || colors.general;
  };

  const getTargetIcon = (targetType) => {
    if (targetType === 'all') return <Users className="w-4 h-4 text-blue-500" />;
    if (targetType === 'bulk') return <Users className="w-4 h-4 text-purple-500" />;
    return <User className="w-4 h-4 text-gray-500" />;
  };

  const getStatusColor = (status) => {
    if (status === 'sent') return 'bg-green-100 text-green-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">Send and manage push notifications to app users</p>
        </div>
        <button
          onClick={() => navigate('/app-control/notifications/send')}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Send className="w-4 h-4" />
          Send Notification
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalDocs}</p>
              <p className="text-xs text-gray-500">Total Sent</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.filter(n => n.status === 'sent').length}
              </p>
              <p className="text-xs text-gray-500">Successful</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.filter(n => n.targetType === 'all').length}
              </p>
              <p className="text-xs text-gray-500">Broadcast</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.filter(n => n.status === 'failed').length}
              </p>
              <p className="text-xs text-gray-500">Failed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          >
            <option value="">All Types</option>
            <option value="booking">Booking</option>
            <option value="payment">Payment</option>
            <option value="promotion">Promotion</option>
            <option value="reminder">Reminder</option>
            <option value="general">General</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors"
          >
            Search
          </button>
          <button
            onClick={() => { setSearchText(''); setFilterType(''); setPage(1); fetchNotifications(); }}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      )}

      {/* Empty State */}
      {!loading && notifications.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No notifications found</p>
          <button
            onClick={() => navigate('/app-control/notifications/send')}
            className="mt-4 text-sm text-black underline"
          >
            Send your first notification
          </button>
        </div>
      )}

      {/* Notifications Table */}
      {!loading && notifications.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Target</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Delivered</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notif) => (
                <tr key={notif._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{notif.body}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(notif.type)}`}>
                      {notif.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {getTargetIcon(notif.targetType)}
                      <span className="text-xs text-gray-600 capitalize">{notif.targetType}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(notif.status)}`}>
                      {notif.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">
                      {notif.successCount || 0}/{(notif.successCount || 0) + (notif.failCount || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500">
                      {new Date(notif.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages} ({totalDocs} total)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationMaster;
